import { useCallback, useEffect, useRef } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore posthog is in the parent dir
import { usePostHog } from 'posthog-js/react';

import {
  captureKitThreadCheckCompleted,
  getPosthogCapture,
} from 'components/Kit/EmailSuggestions/utils/posthogEvents';
import {
  ServiceFeaturesListType,
  isQuickQuoteUiEnabled,
} from 'contexts/serviceContext';
import { useThreadScopedState } from 'hooks/useThreadScopedState';
import {
  KitThreadCheckJob,
  KitThreadCheckKind,
  KitThreadCheckStatus,
  getKitThreadCheck,
  getLatestKitThreadCheck,
  startKitThreadCheck,
} from 'lib/api/kitThreadCheck';
import { Maybe, Undef } from 'types/UtilityTypes';
import { ApiError } from 'types/api/ApiError';
import { pollJob } from 'utils/pollJob';

// A finished check found on thread open is shown only if it is this recent. Older results
// describe a thread that may have moved on, so the chip goes back to idle instead.
export const KIT_THREAD_CHECK_RECENT_RESULT_MS = 30 * 60_000;

// Whether the rep can ask Kit to check a thread for kind. A check only shows its result through the
// sidebar's suggestions, so it needs the view that lists them, and the backend refuses a check for a
// kind the service doesn't extract. TQL's API is still on a legacy branch that does not serve
// thread checks, so those inboxes stay hidden until that branch has the endpoint.
export function isKitThreadCheckAvailable(
  kind: KitThreadCheckKind,
  features: ServiceFeaturesListType,
  isTqlInbox = false
): boolean {
  if (isTqlInbox) {
    return false;
  }

  switch (kind) {
    case KitThreadCheckKind.QuoteRequest:
      return isQuickQuoteUiEnabled(features);
    case KitThreadCheckKind.LoadBuilding:
      return (
        features.isLoadBuildingEnabled &&
        features.isLoadBuildingSuggestionCardsEnabled &&
        !features.disableLoadBuildingProcessing
      );
    default:
      return false;
  }
}

export enum KitThreadCheckPhase {
  Disabled = 'disabled',
  Discovering = 'discovering',
  Idle = 'idle',
  Starting = 'starting',
  Job = 'job',
}

export type KitThreadCheckState =
  | { phase: KitThreadCheckPhase.Disabled }
  | { phase: KitThreadCheckPhase.Discovering }
  | { phase: KitThreadCheckPhase.Idle; startError?: ApiError }
  | { phase: KitThreadCheckPhase.Starting }
  | {
      phase: KitThreadCheckPhase.Job;
      job: KitThreadCheckJob;
      // Set when we stopped polling (job missing or repeated errors); the check may still run.
      pollError?: ApiError;
    };

export type KitThreadCheckController = {
  kind: KitThreadCheckKind;
  state: KitThreadCheckState;
  // Starting or still checking; the chip is disabled and Kit's drum wiggles.
  isRunning: boolean;
  // Ask Kit to (re)check this thread for this kind.
  apply: () => Promise<void>;
  threadId: Maybe<string>;
  emailId: Undef<Maybe<number>>;
};

type UseKitThreadCheckOptions = {
  threadID: Maybe<string>;
  emailID: Undef<Maybe<number>>;
  kind: KitThreadCheckKind;
  enabled: boolean;
  // Called once when a check this rep watched finishes with new suggestions.
  onFoundSuggestions: (threadID: string) => void;
};

const DISCOVERING: KitThreadCheckState = {
  phase: KitThreadCheckPhase.Discovering,
};
const DISABLED: KitThreadCheckState = { phase: KitThreadCheckPhase.Disabled };

// "Ask Kit to check this thread": starts a check and follows it to the end. The server is
// the source of truth, so a rep who leaves the thread and comes back sees the same check.
export function useKitThreadCheck({
  threadID,
  emailID,
  kind,
  enabled,
  onFoundSuggestions,
}: UseKitThreadCheckOptions): KitThreadCheckController {
  const posthog = usePostHog();
  const contextKey =
    enabled && threadID && emailID ? `${threadID}:${kind}` : null;
  // Read by async callbacks to drop results for a thread (or kind) the rep has left.
  const contextKeyRef = useRef(contextKey);
  contextKeyRef.current = contextKey;
  // Keyed by thread and kind, so a previous thread's check never renders after a switch.
  const [storedState, setStateForKey] = useThreadScopedState(
    contextKey,
    DISCOVERING
  );
  const state = contextKey ? storedState : DISABLED;

  // Latest values for async callbacks, so a new closure each render doesn't restart polling.
  const onFoundSuggestionsRef = useRef(onFoundSuggestions);
  onFoundSuggestionsRef.current = onFoundSuggestions;
  const posthogCaptureRef = useRef(getPosthogCapture(posthog));
  posthogCaptureRef.current = getPosthogCapture(posthog);
  // Guards double clicks before the Starting state renders.
  const isStartInFlightRef = useRef(false);

  const job = state.phase === KitThreadCheckPhase.Job ? state.job : null;
  const jobRef = useRef(job);
  jobRef.current = job;
  const pollingJobID =
    state.phase === KitThreadCheckPhase.Job &&
    !state.pollError &&
    isActiveKitThreadCheck(state.job)
      ? state.job.jobId
      : null;

  // Discover the latest check whenever the rep opens a thread (or comes back to one).
  useEffect(() => {
    if (!contextKey || !threadID) {
      return;
    }

    let cancelled = false;
    void getLatestKitThreadCheck(threadID, kind).then((result) => {
      if (cancelled) {
        return;
      }
      // Fail open: if we can't tell, the rep can still ask Kit to check.
      const latest = result.isOk() ? result.value : null;
      setStateForKey(contextKey, (current) =>
        mergeDiscoveredCheck(current, latest)
      );
    });

    return () => {
      cancelled = true;
    };
  }, [contextKey, kind, setStateForKey, threadID]);

  // Poll the active check. Leaving the thread aborts it, so a late result can't land on
  // another thread; coming back rediscovers the check and polls again.
  useEffect(() => {
    const initialJob = jobRef.current;
    if (
      !contextKey ||
      !threadID ||
      !pollingJobID ||
      initialJob?.jobId !== pollingJobID
    ) {
      return;
    }

    const controller = new AbortController();
    void pollJob(initialJob, {
      signal: controller.signal,
      getJob: getKitThreadCheck,
      isActive: isActiveKitThreadCheck,
      // Progress only; the final state is set below so the found callback runs exactly once.
      onUpdate: (updated) => {
        if (isActiveKitThreadCheck(updated)) {
          setStateForKey(contextKey, {
            phase: KitThreadCheckPhase.Job,
            job: updated,
          });
        }
      },
    }).then((outcome) => {
      if (controller.signal.aborted || outcome.type === 'cancelled') {
        return;
      }
      if (outcome.type !== 'completed') {
        setStateForKey(contextKey, {
          phase: KitThreadCheckPhase.Job,
          job: outcome.job,
          pollError:
            outcome.type === 'error'
              ? outcome.error
              : { message: 'Kit lost track of this check.' },
        });
        return;
      }

      setStateForKey(contextKey, {
        phase: KitThreadCheckPhase.Job,
        job: outcome.job,
      });
      captureKitThreadCheckCompleted({
        capture: posthogCaptureRef.current,
        job: outcome.job,
      });
      if (hasNewSuggestions(outcome.job)) {
        onFoundSuggestionsRef.current(threadID);
      }
    });

    return () => controller.abort();
  }, [contextKey, pollingJobID, setStateForKey, threadID]);

  const apply = useCallback(async () => {
    const isRunning =
      state.phase === KitThreadCheckPhase.Starting || pollingJobID !== null;
    if (!contextKey || !threadID || !emailID || isRunning) {
      return;
    }
    if (isStartInFlightRef.current) {
      return;
    }

    isStartInFlightRef.current = true;
    setStateForKey(contextKey, { phase: KitThreadCheckPhase.Starting });

    try {
      const result = await startKitThreadCheck({ threadID, emailID, kind });
      if (contextKeyRef.current !== contextKey) {
        return;
      }
      setStateForKey(
        contextKey,
        result.isOk()
          ? { phase: KitThreadCheckPhase.Job, job: result.value }
          : { phase: KitThreadCheckPhase.Idle, startError: result.error }
      );
    } finally {
      isStartInFlightRef.current = false;
    }
  }, [
    contextKey,
    emailID,
    kind,
    pollingJobID,
    setStateForKey,
    state.phase,
    threadID,
  ]);

  return {
    kind,
    state,
    isRunning:
      state.phase === KitThreadCheckPhase.Starting || pollingJobID !== null,
    apply,
    threadId: threadID,
    emailId: emailID,
  };
}

export function isActiveKitThreadCheck(job: KitThreadCheckJob): boolean {
  return (
    job.status === KitThreadCheckStatus.Pending ||
    job.status === KitThreadCheckStatus.Processing
  );
}

// Failed to start, failed on the server, or we lost track of it: the rep can retry.
export function isFailedState(state: KitThreadCheckState): boolean {
  if (state.phase === KitThreadCheckPhase.Idle) {
    return Boolean(state.startError);
  }
  if (state.phase !== KitThreadCheckPhase.Job) {
    return false;
  }
  return (
    Boolean(state.pollError) || state.job.status === KitThreadCheckStatus.Failed
  );
}

function hasNewSuggestions(job: KitThreadCheckJob): boolean {
  return (
    job.status === KitThreadCheckStatus.Succeeded &&
    (job.result?.newSuggestionCount ?? 0) > 0
  );
}

function isRelevantOnOpen(job: KitThreadCheckJob): boolean {
  if (isActiveKitThreadCheck(job)) {
    return true;
  }
  const finishedAt = Date.parse(job.completedAt ?? job.createdAt);
  return (
    Number.isFinite(finishedAt) &&
    Date.now() - finishedAt < KIT_THREAD_CHECK_RECENT_RESULT_MS
  );
}

// Discover must not wipe a check the rep just started or is still watching when GET latest
// 404s or finishes after POST. Coming back mid-check still picks up an active job from the server.
function mergeDiscoveredCheck(
  current: KitThreadCheckState,
  latest: Maybe<KitThreadCheckJob>
): KitThreadCheckState {
  const discovered =
    latest && isRelevantOnOpen(latest)
      ? ({ phase: KitThreadCheckPhase.Job, job: latest } as const)
      : ({ phase: KitThreadCheckPhase.Idle } as const);

  if (current.phase === KitThreadCheckPhase.Starting) {
    return discovered.phase === KitThreadCheckPhase.Job &&
      isActiveKitThreadCheck(discovered.job)
      ? discovered
      : current;
  }

  if (
    current.phase === KitThreadCheckPhase.Job &&
    isActiveKitThreadCheck(current.job)
  ) {
    return discovered.phase === KitThreadCheckPhase.Job ? discovered : current;
  }

  return discovered;
}
